import React, { useState, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  InputAdornment,
  Chip,
  IconButton,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useInventory } from '../../contexts/InventoryContext';
import { FURNITURE_CATEGORIES as categories } from '../../utils/categories';
import { normalizeCategory } from '../../utils/categorize';

const subcategories = {
  electronics: ['phones', 'laptops', 'tablets', 'accessories', 'other'],
  clothing: ['shirts', 'pants', 'shoes', 'accessories', 'other'],
  food: ['perishable', 'non-perishable', 'beverages', 'snacks', 'other'],
  books: ['fiction', 'non-fiction', 'educational', 'magazines', 'other'],
  home: ['furniture', 'appliances', 'decor', 'kitchen', 'other'],
  sports: ['equipment', 'apparel', 'footwear', 'accessories', 'other'],
  beauty: ['skincare', 'makeup', 'haircare', 'fragrance', 'other'],
  automotive: ['parts', 'accessories', 'tools', 'fluids', 'other'],
  toys: ['action-figures', 'dolls', 'games', 'puzzles', 'other'],
  furniture: ['chairs', 'tables', 'beds', 'storage', 'other'],
  office: ['supplies', 'furniture', 'electronics', 'stationery', 'other'],
  outdoor: ['camping', 'gardening', 'sports', 'tools', 'other'],
  other: ['miscellaneous']
};

const EditItemModal = ({ open, onClose, onSubmit, item, selectedLocation }) => {
  const { error, clearError } = useInventory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    subcategory: '',
    description: '',
    brand: '',
    model: '',
    pricing: {
      retail: '',
      cost: ''
    },
    stock: {
      available: '',
      reorderPoint: '',
      maxQuantity: ''
    },
    status: 'active'
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  // Initialize form with item data
  useEffect(() => {
    if (item && open) {
      setFormData({
        name: item.name || '',
        sku: item.sku || '',
        category: normalizeCategory(item.category || ''),
        subcategory: item.subcategory || '',
        description: item.description || '',
        brand: item.brand || '',
        model: item.model || '',
        pricing: {
          retail: item.pricing?.retail || item.retail || '',
          cost: item.pricing?.cost || item.cost || ''
        },
        stock: {
          available: item.stock?.available || item.quantity || '',
          reorderPoint: item.stock?.reorderPoint || item.reorderPoint || '',
          maxQuantity: item.stock?.maxQuantity || item.maxQuantity || ''
        },
        status: item.status || 'active'
      });
      
      // Set image previews from existing images
      if (item.images && Array.isArray(item.images)) {
        setImagePreviews(item.images.map(img => ({
          url: img.url,
          isExisting: true
        })));
      } else {
        setImagePreviews([]);
      }
      
      // Clear any previous errors
      clearError();
      setValidationErrors({});
    }
  }, [item, open, clearError]);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Item name is required';
    }
    
    if (!formData.sku.trim()) {
      errors.sku = 'SKU is required';
    }
    
    if (!formData.category) {
      errors.category = 'Category is required';
    }
    
    const hasSubcats = !!subcategories[formData.category];
    if (hasSubcats && !formData.subcategory) {
      errors.subcategory = 'Subcategory is required';
    }
    
    if (!formData.pricing.retail || parseFloat(formData.pricing.retail) <= 0) {
      errors.retail = 'Retail price must be a positive number';
    }
    
    if (formData.pricing.cost && parseFloat(formData.pricing.cost) < 0) {
      errors.cost = 'Cost price cannot be negative';
    }
    
    if (formData.stock.available === '' || parseInt(formData.stock.available) < 0) {
      errors.available = 'Available stock must be a non-negative integer';
    }
    
    if (formData.stock.reorderPoint && parseInt(formData.stock.reorderPoint) < 0) {
      errors.reorderPoint = 'Reorder point must be a non-negative integer';
    }
    
    if (formData.stock.maxQuantity && parseInt(formData.stock.maxQuantity) < 0) {
      errors.maxQuantity = 'Max quantity must be a non-negative integer';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested fields
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Add new images to previews
    const newPreviews = files.map(file => ({
      url: URL.createObjectURL(file),
      file: file,
      isExisting: false
    }));
    
    setImagePreviews(prev => [...prev, ...newPreviews]);
    setNewImages(prev => [...prev, ...files]);
  };

  const removeImage = (index) => {
    const updatedPreviews = [...imagePreviews];
    const removedImage = updatedPreviews.splice(index, 1)[0];
    
    // Revoke object URL to prevent memory leaks
    if (removedImage.url && !removedImage.isExisting) {
      URL.revokeObjectURL(removedImage.url);
    }
    
    setImagePreviews(updatedPreviews);
    
    // If it's a new image, also remove from newImages array
    if (!removedImage.isExisting) {
      const updatedNewImages = [...newImages];
      const fileIndex = updatedNewImages.findIndex(file => file === removedImage.file);
      if (fileIndex !== -1) {
        updatedNewImages.splice(fileIndex, 1);
        setNewImages(updatedNewImages);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    clearError();
    setValidationErrors({});
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Prepare data for submission
    const submitData = {
      ...formData,
      pricing: {
        retail: parseFloat(formData.pricing.retail),
        cost: formData.pricing.cost ? parseFloat(formData.pricing.cost) : null
      },
      stock: {
        available: parseInt(formData.stock.available),
        reorderPoint: formData.stock.reorderPoint ? parseInt(formData.stock.reorderPoint) : 0,
        maxQuantity: formData.stock.maxQuantity ? parseInt(formData.stock.maxQuantity) : null
      }
    };
    
    // Submit the form
    await onSubmit(submitData, newImages, item.id);
    
    // Close modal if successful
    if (!error) {
      handleClose();
    }
  };

  const handleClose = () => {
    // Revoke all object URLs to prevent memory leaks
    imagePreviews.forEach(preview => {
      if (preview.url && !preview.isExisting) {
        URL.revokeObjectURL(preview.url);
      }
    });
    
    setImagePreviews([]);
    setNewImages([]);
    setFormData({
      name: '',
      sku: '',
      category: '',
      subcategory: '',
      description: '',
      brand: '',
      model: '',
      pricing: {
        retail: '',
        cost: ''
      },
      stock: {
        available: '',
        reorderPoint: '',
        maxQuantity: ''
      },
      status: 'active'
    });
    
    clearError();
    setValidationErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        Edit Inventory Item
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SKU"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                error={!!validationErrors.sku}
                helperText={validationErrors.sku}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Brand"
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Model"
                name="model"
                value={formData.model}
                onChange={handleInputChange}
              />
            </Grid>
            
            {/* Category */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!validationErrors.category}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  label="Category"
                  required
                >
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.category && (
                  <Typography variant="caption" color="error">
                    {validationErrors.category}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!validationErrors.subcategory}>
                <InputLabel>Subcategory</InputLabel>
                <Select
                  name="subcategory"
                  value={formData.subcategory}
                  onChange={handleInputChange}
                  label="Subcategory"
                  required={!!subcategories[formData.category]}
                  disabled={!formData.category || !subcategories[formData.category]}
                >
                  {formData.category && subcategories[formData.category] ? (
                    subcategories[formData.category].map(sub => (
                      <MenuItem key={sub} value={sub}>
                        {sub.charAt(0).toUpperCase() + sub.slice(1)}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No subcategories</MenuItem>
                  )}
                </Select>
                {validationErrors.subcategory && (
                  <Typography variant="caption" color="error">
                    {validationErrors.subcategory}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* Pricing */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Pricing
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Retail Price"
                name="pricing.retail"
                type="number"
                value={formData.pricing.retail}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                error={!!validationErrors.retail}
                helperText={validationErrors.retail}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost Price"
                name="pricing.cost"
                type="number"
                value={formData.pricing.cost}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                error={!!validationErrors.cost}
                helperText={validationErrors.cost}
              />
            </Grid>
            
            {/* Stock Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Stock Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Available Stock"
                name="stock.available"
                type="number"
                value={formData.stock.available}
                onChange={handleInputChange}
                error={!!validationErrors.available}
                helperText={validationErrors.available}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Reorder Point"
                name="stock.reorderPoint"
                type="number"
                value={formData.stock.reorderPoint}
                onChange={handleInputChange}
                error={!!validationErrors.reorderPoint}
                helperText={validationErrors.reorderPoint}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Max Quantity"
                name="stock.maxQuantity"
                type="number"
                value={formData.stock.maxQuantity}
                onChange={handleInputChange}
                error={!!validationErrors.maxQuantity}
                helperText={validationErrors.maxQuantity}
              />
            </Grid>
            
            {/* Status */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Status
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="discontinued">Discontinued</MenuItem>
                  <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Images */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Images
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="edit-image-upload"
                type="file"
                onChange={handleImageChange}
                multiple
              />
              <label htmlFor="edit-image-upload">
                <Button 
                  variant="outlined" 
                  component="span" 
                  startIcon={<AddIcon />}
                >
                  Add Images
                </Button>
              </label>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {imagePreviews.map((preview, index) => (
                  <Grid item xs={6} sm={3} key={index}>
                    <Box sx={{ position: 'relative' }}>
                      <img
                        src={preview.url}
                        alt={`Preview ${index}`}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeImage(index)}
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          bgcolor: 'background.paper',
                          boxShadow: 1
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      {preview.isExisting && (
                        <Chip 
                          label="Existing" 
                          size="small" 
                          sx={{ 
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            borderRadius: '4px 0'
                          }} 
                        />
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditItemModal;
